import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import RemoveQueuedArtworkConfirmationPopup from './RemovedQueuedArticleConfirmationPopup';

import './styles/QueuedArtworksPopup.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

function formatQueueDate(isoString) {
  if (!isoString) return '—';
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return '—';

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: MADRID_TIMEZONE
  }).format(parsed);
}

export default function QueuedArtworksPopup({
  open,
  onClose,
  drafts,
  artworks,
  onDraftOpen,
  onArtworkOpen,
  onDraftDelete,
  onArtworkDelete
}) {
  const t = useTranslations('common.profile.popups.queued_artworks');
  const [pendingItem, setPendingItem] = useState(null);
  const [removedDraftIds, setRemovedDraftIds] = useState([]);
  const [removedArtworkIds, setRemovedArtworkIds] = useState([]);

  const handleBackgroundClick = useCallback(event => {
    const card = event.currentTarget.querySelector('.generic-popup__card');
    if (card && card.contains(event.target)) {
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (removedDraftIds.length === 0) return;
    const activeIds = new Set(drafts.map(item => item._id));
    setRemovedDraftIds(prev => prev.filter(id => activeIds.has(id)));
  }, [drafts, removedDraftIds]);

  useEffect(() => {
    if (removedArtworkIds.length === 0) return;
    const activeIds = new Set(artworks.map(item => item._id));
    setRemovedArtworkIds(prev => prev.filter(id => activeIds.has(id)));
  }, [artworks, removedArtworkIds]);

  const queueItems = useMemo(() => {
    const removedDraftSet = new Set(removedDraftIds);
    const removedArtworkSet = new Set(removedArtworkIds);
    const normalizedDrafts = drafts
      .filter(item => !removedDraftSet.has(item._id))
      .map(item => ({
        _id: item._id,
        type: 'draft',
        title: item.draft_container?.artwork_about?.artwork_title || t('fallback.untitled_artwork'),
        scheduled: item.publishedAt ?? item.draft_container?.publishedAt,
        raw: item
      }));

    const normalizedArtworks = artworks
      .filter(item => !removedArtworkSet.has(item._id))
      .map(item => ({
        _id: item._id,
        type: 'artwork',
        title: item?.artwork_about?.artwork_title || t('fallback.untitled_artwork'),
        scheduled: item.publishedAt ?? item.scheduleDate,
        raw: item
      }));

    return [...normalizedDrafts, ...normalizedArtworks].sort((a, b) => {
      const aDate = Date.parse(a.scheduled ?? '');
      const bDate = Date.parse(b.scheduled ?? '');
      if (Number.isNaN(aDate) && Number.isNaN(bDate)) return 0;
      if (Number.isNaN(aDate)) return 1;
      if (Number.isNaN(bDate)) return -1;
      return aDate - bDate;
    });
  }, [artworks, drafts, removedArtworkIds, removedDraftIds]);

  const handleItemOpen = useCallback(item => {
    if (item.type === 'draft' && typeof onDraftOpen === 'function') {
      onDraftOpen(item.raw);
    }

    if (item.type === 'artwork' && typeof onArtworkOpen === 'function') {
      onArtworkOpen(item.raw);
    }
  }, [onArtworkOpen, onDraftOpen]);

  const handleRemoveItem = useCallback((event, item) => {
    event?.stopPropagation?.();
    const isDraft = item.type === 'draft';
    const hasHandler = isDraft
      ? typeof onDraftDelete === 'function'
      : typeof onArtworkDelete === 'function';

    if (!hasHandler) {
      return;
    }

    setPendingItem(item);
  }, [onArtworkDelete, onDraftDelete]);

  const handleConfirmDelete = useCallback(async () => {
    const item = pendingItem;
    if (!item) {
      return;
    }

    try {
      if (item.type === 'draft' && typeof onDraftDelete === 'function') {
        await onDraftDelete(item.raw);
        setRemovedDraftIds(prev => (prev.includes(item._id) ? prev : [...prev, item._id]));
        return;
      }

      if (item.type === 'artwork' && typeof onArtworkDelete === 'function') {
        await onArtworkDelete(item.raw);
        setRemovedArtworkIds(prev => (prev.includes(item._id) ? prev : [...prev, item._id]));
      }
    } catch (error) {
      console.error('Failed to delete queued item', error);
      throw error;
    }
  }, [onArtworkDelete, onDraftDelete, pendingItem]);

  return (
    <GenericPopup
      open={open}
      className="queued-artworks-popup remove-scrollbar"
      darkerBlur
      opacity
      onBackgroundClick={handleBackgroundClick}
    >
      <div className="queued-artworks-popup__header">
        <Text className="queued-artworks-popup__title font-size-large">
          {t('title')}
        </Text>
        <XIcon className="queued-artworks-popup__close" onClick={onClose} />
      </div>
      <div className="queued-artworks-popup__list remove-scrollbar">
        {queueItems.length === 0 ? (
          <Text className="queued-artworks-popup__empty font-size-small">
            {t('empty')}
          </Text>
        ) : (
          queueItems.map(item => {
            const isDraft = item.type === 'draft';
            const canRemove = isDraft
              ? typeof onDraftDelete === 'function'
              : typeof onArtworkDelete === 'function';

            return (
              <div
                key={item._id}
                className="queued-artworks-popup__item element-non-selectable"
                role="button"
                tabIndex={0}
                onClick={() => handleItemOpen(item)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleItemOpen(item);
                  }
                }}
              >
                <button
                  type="button"
                  className={`queued-artworks-popup__remove-button${canRemove ? '' : ' queued-artworks-popup__remove-button--disabled'}`}
                  aria-label={canRemove ? t(isDraft ? 'aria.remove_queued_draft' : 'aria.remove_queued_artwork') : undefined}
                  aria-hidden={canRemove ? undefined : true}
                  onClick={event => handleRemoveItem(event, item)}
                  disabled={!canRemove}
                >
                  <span />
                </button>
                <div className="queued-artworks-popup__content">
                  <Text
                    className="queued-artworks-popup__item-title dots-on-overflow font-size-medium">
                    {item.title}
                  </Text>
                  <Text className="queued-artworks-popup__item-date font-size-small">
                    {formatQueueDate(item.scheduled)}
                  </Text>
                </div>
              </div>
            );
          })
        )}
      </div>
      {pendingItem && (
        <RemoveQueuedArtworkConfirmationPopup
          open
          onConfirm={handleConfirmDelete}
          onClose={() => setPendingItem(null)}
        />
      )}
    </GenericPopup>
  );
}

QueuedArtworksPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  drafts: PropTypes.arrayOf(PropTypes.object),
  artworks: PropTypes.arrayOf(PropTypes.object),
  onDraftOpen: PropTypes.func,
  onArtworkOpen: PropTypes.func,
  onDraftDelete: PropTypes.func,
  onArtworkDelete: PropTypes.func
};

QueuedArtworksPopup.defaultProps = {
  open: false,
  onClose: undefined,
  drafts: [],
  artworks: [],
  onDraftOpen: undefined,
  onArtworkOpen: undefined,
  onDraftDelete: undefined,
  onArtworkDelete: undefined
};
